import React, { useState } from 'react';
import { CheckIcon } from './icons/CheckIcon';
import { initiateCheckout } from '../services/stripeService';
import { useAuth } from '../contexts/AuthContext';
import {
  PRICING_TIERS,
  SALES_EMAIL,
  getDisplayPrice,
  getPeriodLabel,
  getSavingsLabel,
  type BillingPeriod,
} from '../config/pricing';

const pricingFont = { fontFamily: "'Sora', ui-sans-serif, system-ui, sans-serif" };

interface PricingTiersProps {
  onStartFreeTrial?: () => void;
}

const PricingTiers: React.FC<PricingTiersProps> = ({ onStartFreeTrial }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  const handlePurchase = async (tierId: string) => {
    let plan: 'day_pass' | 'monthly_pro' | 'annual_pro';

    if (tierId === 'day_pass') {
      plan = 'day_pass';
    } else if (tierId === 'pro_monthly') {
      plan = billingPeriod === 'annual' ? 'annual_pro' : 'monthly_pro';
    } else {
      return;
    }

    if (!user) {
      alert('Please sign in to purchase a plan.');
      return;
    }

    setLoading(tierId);
    try {
      await initiateCheckout(plan);
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const handleContactSales = (planName = 'District') => {
    window.location.href = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(planName + ' Plan Inquiry')}&body=${encodeURIComponent(`I'm interested in learning more about the ${planName} plan for Redact AI.`)}`;
  };

  const individualTiers = PRICING_TIERS.filter((t) =>
    ['free', 'day_pass', 'pro_monthly'].includes(t.id)
  );
  const teamTiers = PRICING_TIERS.filter((t) =>
    ['campus_monthly', 'district'].includes(t.id)
  );

  const renderTierCard = (tier: (typeof PRICING_TIERS)[0], isWide = false) => {
    const displayPrice = getDisplayPrice(tier, billingPeriod);
    const periodLabel = getPeriodLabel(tier, billingPeriod);
    const savingsLabel = getSavingsLabel(tier, billingPeriod);
    const isDisabled = tier.id === 'free';
    const showSavings = billingPeriod === 'annual' && savingsLabel && !tier.isContactSales;
    const isPro = tier.id === 'pro_monthly';
    const isProMonthlyPromo = isPro && billingPeriod === 'monthly';
    const isProAnnualPromo =
      isPro &&
      billingPeriod === 'annual' &&
      tier.annualPrice != null &&
      tier.monthlyPrice != null;

    const cardBorderClass = isPro
      ? 'border-[1.5px] border-solid border-[#cf9738]'
      : tier.isPopular
        ? 'border-2 border-emerald-500'
        : 'border-2 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600';

    const cardInner = (
      <div
        className={`relative flex flex-col p-8 bg-white dark:bg-slate-800 rounded-2xl shadow-lg ${cardBorderClass} transition-all ${
          !isPro && tier.isPopular ? 'scale-105' : ''
        } ${isWide ? 'md:col-span-1' : ''}`}
        style={pricingFont}
      >
        {tier.badge && (
          <div
            className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full px-4 py-1.5 text-sm font-semibold text-white shadow-lg ${
              tier.isPopular ? 'bg-emerald-500' : 'bg-blue-500'
            }`}
          >
            {tier.badge}
          </div>
        )}
        <div className="mb-4">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white">{tier.name}</h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{tier.tagline}</p>
        </div>
        <div className="mb-6">
          {isProMonthlyPromo && tier.listMonthlyPrice != null ? (
            <>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span
                  className="text-base font-medium leading-none"
                  style={{
                    color: '#888888',
                    textDecoration: 'line-through',
                  }}
                >
                  ${tier.listMonthlyPrice?.toFixed(2)}/month
                </span>
                <span className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                  ${tier.monthlyPrice?.toFixed(2)}/month
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Locks in your rate before we raise pricing
              </p>
            </>
          ) : isProAnnualPromo ? (
            <>
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span
                  className="text-base font-medium leading-none"
                  style={{
                    color: '#888888',
                    textDecoration: 'line-through',
                  }}
                >
                  ${(tier.monthlyPrice! * 12).toFixed(2)}
                </span>
                <span className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                  ${tier.annualPrice}
                </span>
                {periodLabel && (
                  <span className="text-base font-medium text-slate-500 dark:text-slate-400">
                    {periodLabel}
                  </span>
                )}
              </div>
              {savingsLabel && (
                <p className="mt-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                  {savingsLabel}
                </p>
              )}
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                ${(tier.annualPrice! / 12).toFixed(2)}/mo equivalent — billed once per year
              </p>
            </>
          ) : (
            <>
              <span className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                {displayPrice}
              </span>
              {periodLabel && (
                <span className="text-base font-medium text-slate-500 dark:text-slate-400">
                  {periodLabel}
                </span>
              )}
              {showSavings && (
                <span className="ml-2 inline-block px-2 py-0.5 text-xs font-semibold text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/30 rounded-full">
                  {savingsLabel}
                </span>
              )}
            </>
          )}
        </div>
        <ul role="list" className="space-y-3 flex-grow mb-8">
          {tier.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3">
              <CheckIcon className="h-5 w-5 flex-shrink-0 text-emerald-500 mt-0.5" />
              <span className="text-slate-600 dark:text-slate-300 text-sm">{feature}</span>
            </li>
          ))}
        </ul>
        {tier.isContactSales ? (
          <button
            onClick={() => handleContactSales(tier.name)}
            className="w-full rounded-lg px-4 py-3 text-center text-base font-semibold transition-all bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white"
          >
            Request a Quote
          </button>
        ) : (
          <>
            <button
              onClick={() => {
                if (!isDisabled) {
                  const shouldOpenCreateAccount =
                    tier.id === 'pro_monthly' && billingPeriod === 'monthly' && !!onStartFreeTrial;
                  if (shouldOpenCreateAccount) {
                    onStartFreeTrial();
                    return;
                  }
                  handlePurchase(tier.id);
                }
              }}
              disabled={isDisabled || loading !== null}
              className={`w-full rounded-lg px-4 py-3 text-center text-base font-semibold transition-all ${
                isPro
                  ? 'text-white shadow-lg hover:opacity-95'
                  : tier.isPopular
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/25'
                    : isDisabled
                      ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-default'
                      : 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
              style={
                isPro
                  ? { backgroundColor: 'var(--tli-green, #15452f)' }
                  : undefined
              }
            >
              {loading === tier.id ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Processing...
                </span>
              ) : isDisabled ? (
                'Current Plan'
              ) : tier.id === 'day_pass' ? (
                'Get 24-Hour Access'
              ) : tier.id === 'pro_monthly' ? (
                billingPeriod === 'annual' ? (
                  'Subscribe Annually'
                ) : (
                  'Try Pro Free for 7 Days'
                )
              ) : (
                'Get Started'
              )}
            </button>
            {isProMonthlyPromo && (
              <p className="mt-3 text-center text-sm text-slate-600 dark:text-slate-400">
                Then $9.99/month. Cancel anytime.
              </p>
            )}
          </>
        )}
      </div>
    );

    if (isProMonthlyPromo) {
      return (
        <div key={tier.id} className={`flex flex-col ${isWide ? 'md:col-span-1' : ''}`}>
          <div className="mb-2 flex justify-center">
            <span
              className="inline-block rounded-full px-3 py-1 font-semibold"
              style={{
                backgroundColor: '#cf9738',
                color: '#15452f',
                fontSize: '11px',
                fontFamily: "'Sora', sans-serif",
              }}
            >
              Early Adopter Price
            </span>
          </div>
          {cardInner}
        </div>
      );
    }

    return (
      <div key={tier.id} className={isWide ? 'md:col-span-1' : ''}>
        {cardInner}
      </div>
    );
  };

  return (
    <section className="py-12 mt-12" style={pricingFont}>
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          Simple, Transparent Pricing
        </h2>
        <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
          Prepare educational documents for AI tools — designed for educators, evaluation teams, and SPED professionals.
        </p>
      </div>

      <div className="mt-8 flex justify-center">
        <div className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 p-1">
          <button
            type="button"
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-2 text-sm font-medium rounded-full transition-all ${
              billingPeriod === 'monthly'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingPeriod('annual')}
            className={`px-6 py-2 text-sm font-medium rounded-full transition-all ${
              billingPeriod === 'annual'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Annual
            <span className="ml-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-semibold">
              Save vs monthly
            </span>
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto px-4">
        {individualTiers.map((tier) => renderTierCard(tier))}
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto px-4">
        {teamTiers.map((tier) => renderTierCard(tier, true))}
      </div>

      <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
        Secure payments powered by Stripe. Cancel anytime.
      </p>
    </section>
  );
};

export default PricingTiers;
